import { inject, Injectable, signal } from "@angular/core";

import { lastValueFrom, Observable } from "rxjs";

import { CommonConstants } from "@shared/constants";
import { RecordStateEnum } from "@shared/enums";
import { IPurchaseOrdersGraphQLFilter, IRecordState } from "@shared/interfaces";
import { getPurchaseOrdersByIdsGraphQLQuery, getPurchaseOrdersGraphQLQuery } from "@shared/queries";
import { ApiService, AuthenticationService } from "@shared/services";
import { GraphService } from "@shared/services/api/graph.service";
import { APP_CONFIG } from "@shared/tokens";
import { FormUtils } from "@shared/utils";

import { PageablePurchaseOrders } from "./models/pageable-purchase-order.type";
import { PurchaseOrderParams } from "./models/purchase-order-params.type";
import { PurchaseOrderPayload } from "./models/purchase-order-payload.type";
import {
  ILineITem,
  IPurchaseOrder,
  IPurchaseOrderByIdsGraphQLResponse,
  IPurchaseOrderExtended,
  IPurchaseOrderGraphQLResponse,
} from "./models/purchase-order.interface";

@Injectable({
  providedIn: "root",
})
export class PurchaseOrdersService {
  private readonly api = inject(ApiService);

  private readonly authService = inject(AuthenticationService);

  private readonly environment = inject(APP_CONFIG);

  private readonly graphService = inject(GraphService);

  private currentPurchaseOrder = signal<IPurchaseOrderExtended>(null);

  private readonly getBaseUrl = () =>
    `${this.environment.baseUrl}organisations/${this.authService.getActiveOrganisationId()}/purchase-orders`;

  getAll(params: PurchaseOrderParams) {
    const query = FormUtils.addUrlParams(params);

    return lastValueFrom(this.api.get<PageablePurchaseOrders>(`${this.getBaseUrl()}?${query}`));
  }

  get(id: string) {
    return lastValueFrom(this.api.get<IPurchaseOrder>(`${this.getBaseUrl()}/${id}`));
  }

  public createOrUpdate = async (payload: any, id?: string): Promise<IPurchaseOrder> => {
    if (id) {
      return await lastValueFrom(
        this.api.put<IPurchaseOrder>(`${this.getBaseUrl()}/${id}`, payload),
      );
    } else {
      return await lastValueFrom(this.api.post<IPurchaseOrder>(`${this.getBaseUrl()}`, payload));
    }
  };

  public async getAllGraphQL(
    filter: IPurchaseOrdersGraphQLFilter = null,
    first: number = CommonConstants.MAX_API_GET_ITEMS_SIZE,
    include: string[] = [],
  ): Promise<any[]> {
    const result: any[] = [];
    let cursor: string | undefined = undefined;
    let hasNextPage: boolean = true;
    const activeOrganisationId = this.authService.getActiveOrganisationId();

    while (hasNextPage) {
      const query = getPurchaseOrdersGraphQLQuery(
        activeOrganisationId,
        filter,
        first,
        null,
        cursor,
        null,
        include,
      );
      const { content, pageInfo, totalCount } = await this.graphService.fetchSinglePage<
        IPurchaseOrderGraphQLResponse,
        IPurchaseOrderExtended
      >("loadPurchaseOrders", query, this.graphQLPropertiesToAddFunction);

      result.push(...content);

      cursor = pageInfo.endCursor;
      hasNextPage = result.length < totalCount;
    }

    return result;
  }

  public async getByIdsGraphQL(
    ids: string[],
    first: number = CommonConstants.MAX_API_GET_ITEMS_SIZE,
    include: string[] = [],
  ): Promise<IPurchaseOrderExtended[]> {
    if (!ids.length) {
      return [];
    }
    const result: IPurchaseOrderExtended[] = [];
    let cursor: string | undefined = undefined;
    let hasNextPage: boolean = true;
    const activeOrganisationId = this.authService.getActiveOrganisationId();

    while (hasNextPage) {
      const query = getPurchaseOrdersByIdsGraphQLQuery(
        activeOrganisationId,
        ids,
        first,
        null,
        cursor,
        null,
        include,
      );
      const { content, pageInfo, totalCount } = await this.graphService.fetchSinglePage<
        IPurchaseOrderByIdsGraphQLResponse,
        IPurchaseOrderExtended
      >("loadPurchaseOrdersByIds", query, this.graphQLPropertiesToAddFunction);

      result.push(...content);

      cursor = pageInfo.endCursor;
      hasNextPage = result.length < totalCount;
    }

    return result;
  }

  public delete = async (id: string): Promise<void> =>
    await lastValueFrom(this.api.delete<void>(`${this.getBaseUrl()}/${id}`));

  setCurrentPurchaseOrder(purchaseOrder: IPurchaseOrderExtended): void {
    this.currentPurchaseOrder.set(purchaseOrder);
  }

  upsertItem(item: ILineITem, oldLineItemId: string = null) {
    const payload = this.getPayloadFromPurchaseOrder();

    if (oldLineItemId) {
      const index = payload.lineItems.findIndex((line) => line.lineItemId === oldLineItemId);

      payload.lineItems[index] = item;
    } else {
      payload.lineItems = [...payload.lineItems, item];
    }

    return lastValueFrom(
      this.api.put<IPurchaseOrder>(
        `${this.getBaseUrl()}/${this.currentPurchaseOrder().id}`,
        payload,
      ),
    );
  }

  removeLineItem(lineItemId: string) {
    const payload = this.getPayloadFromPurchaseOrder();

    payload.lineItems = payload.lineItems.filter((item) => item.lineItemId !== lineItemId);

    return lastValueFrom(
      this.api.put<IPurchaseOrder>(
        `${this.getBaseUrl()}/${this.currentPurchaseOrder().id}`,
        payload,
      ),
    );
  }

  private getPayloadFromPurchaseOrder() {
    const purchaseOrder = this.currentPurchaseOrder();
    const activeOrgId = this.authService.getActiveOrganisationId();
    const buyerUri = purchaseOrder.buyer
      ? `/organisations/${activeOrgId}/connections/${purchaseOrder.buyer.id}`
      : `/organisations/${activeOrgId}`;
    const supplierUri = purchaseOrder.supplier
      ? `/organisations/${activeOrgId}/connections/${purchaseOrder.supplier.id}`
      : `/organisations/${activeOrgId}`;
    const deliverToLocationUri = purchaseOrder.deliverToLocation
      ? `/organisations/${activeOrgId}/locations/${purchaseOrder.deliverToLocation.id}`
      : undefined;

    const status = purchaseOrder.status;
    const lineItems = purchaseOrder.lineItems.length
      ? purchaseOrder.lineItems.map((item) => ({
          lineItemId: item.lineItemId,
          product: `/organisations/${activeOrgId}/products/${item.product.id}`,
          quantity: item.quantity,
        }))
      : [];

    const payload: PurchaseOrderPayload = {
      number: purchaseOrder.number,
      orderDate: purchaseOrder.orderDate,
      expectedDeliveryDate: purchaseOrder.expectedDeliveryDate,
      note: purchaseOrder.note,
      buyer: buyerUri,
      supplier: supplierUri,
      status,
      deliverToLocation: deliverToLocationUri,
      lineItems,
    };

    return payload;
  }

  public getPageSubscription = (
    number: string = undefined,
    size: number = CommonConstants.MAX_API_GET_ITEMS_SIZE,
    page = 0,
    sort: string = undefined,
    recordState: RecordStateEnum = undefined,
  ): Observable<PageablePurchaseOrders> => {
    const url = `${this.getBaseUrl()}?${FormUtils.addUrlParams({ number, size, page, sort, recordState })}`;

    return this.api.get<PageablePurchaseOrders>(url);
  };

  public setRecordState = async (
    payload: IRecordState,
    id: string,
  ): Promise<IPurchaseOrderExtended> =>
    await lastValueFrom(
      this.api.put<IPurchaseOrderExtended>(`${this.getBaseUrl()}/${id}`, payload),
    );

  private graphQLPropertiesToAddFunction = (edge: any): any => {
    return {
      documents: edge.node.documents,
    };
  };
}
